import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, PaperPlaneTilt, Spinner, Robot, ChatCircle } from "@phosphor-icons/react";

/**
 * Panel lateral de chat sobre un análisis específico.
 * Props:
 *   - kind: "inventory" | "marketing" | "purchasing"
 *   - analysisId: string
 *   - onClose
 */
export default function AnalysisChatPanel({ kind, analysisId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/alendra/analyses/${kind}/${analysisId}/chat`)
      .then((r) => { if (!cancelled) setMessages(r.data.messages || []); })
      .catch(() => { if (!cancelled) toast.error("No se pudo cargar el chat"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [kind, analysisId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setMessages((prev) => [...prev, { role: "user", content: text, at: new Date().toISOString() }]);
    setInput("");
    try {
      const { data } = await api.post(`/alendra/analyses/${kind}/${analysisId}/chat`, { message: text });
      setMessages(data.messages || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar mensaje");
      // Quitar el mensaje optimista si falló
      setMessages((prev) => prev.slice(0, -1));
    } finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={onClose} data-testid="analysis-chat-panel">
      <div className="bg-white w-full max-w-md h-full shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2 bg-gradient-to-r from-[#002FA7] to-purple-700 text-white">
          <Robot size={20} weight="fill" className="text-amber-300"/>
          <div className="flex-1">
            <div className="font-bold text-sm">Chat con Alendra AI</div>
            <div className="text-[10px] opacity-80 uppercase tracking-wider">{kind === "inventory" ? "Sobre Inventario" : kind === "marketing" ? "Sobre Marketing" : "Sobre Compras"}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="chat-close">
            <X size={16}/>
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-zinc-50">
          {loading && (
            <div className="text-center text-xs text-zinc-400"><Spinner size={16} className="animate-spin inline mr-2"/>Cargando chat…</div>
          )}
          {!loading && messages.length === 0 && (
            <div className="text-center text-xs text-zinc-500 py-8" data-testid="chat-empty">
              <ChatCircle size={36} className="inline mb-2 text-zinc-300"/>
              <p>Pregúntale a Alendra sobre este análisis.</p>
              <p className="mt-2">Ejemplos:</p>
              <ul className="text-left max-w-[260px] mx-auto mt-1 space-y-1 italic text-zinc-400">
                <li>&bull; &ldquo;¿Por qué me sugieres ese combo?&rdquo;</li>
                <li>&bull; &ldquo;Olvidaste el power bank, ¿qué hacemos?&rdquo;</li>
                <li>&bull; &ldquo;Dame 3 hooks más fuertes para el reel&rdquo;</li>
              </ul>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${i}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.role === "user" ? "bg-[#002FA7] text-white" : "bg-white border border-zinc-200 text-zinc-800"}`} data-testid={`chat-message-${m.role}-${i}`}>
                <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                <div className={`text-[9px] mt-1 ${m.role === "user" ? "text-white/60" : "text-zinc-400"}`}>
                  {m.at ? new Date(m.at).toLocaleTimeString("es-MX", { hour:"2-digit", minute:"2-digit" }) : ""}
                </div>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="chat-typing">
              <div className="bg-white border border-zinc-200 rounded-lg px-3 py-2 text-xs text-zinc-500">
                <Spinner size={12} className="animate-spin inline mr-1"/>Alendra está pensando…
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-zinc-200 p-3 bg-white">
          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Pregúntale a Alendra sobre este análisis…"
              rows={2}
              data-testid="chat-input"
              className="flex-1 border border-zinc-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] resize-none"
            />
            <button
              type="button"
              onClick={send}
              disabled={!input.trim() || sending}
              data-testid="chat-send"
              className="bg-[#002FA7] hover:bg-[#002277] text-white rounded px-3 disabled:opacity-50"
              title="Enviar (Enter)"
            >
              <PaperPlaneTilt size={18} weight="fill"/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
